import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, switchMap, tap, map } from 'rxjs/operators';
import {
  AuthService,
  ImpersonationService,
  PermissionCatalogService,
  PermissionService,
  UserService,
} from '@niko-crm/ui';
import { LoggedUserActions } from './logged-user.actions';
import { of } from 'rxjs';
import { extractError } from '../helpers/error-reader';

@Injectable()
export class LoggedUserEffects {
  private readonly actions$ = inject(Actions);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCatalog = inject(PermissionCatalogService);
  private readonly userService = inject(UserService);
  private readonly impersonationService = inject(ImpersonationService);
  protected readonly authService = inject(AuthService);

  syncPermissions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoggedUserActions.loginSuccess),
        tap(({ permissions, isSuperAdmin }) => {
          this.permissionService.setPermissions(permissions);
          this.permissionService.setSuperAdmin(isSuperAdmin ?? false);
          // SuperAdmin bypasses PermissionsGuard, so it never hits a 403 that
          // needs a human label — skip the extra GET /permissions fetch.
          if (!isSuperAdmin) this.permissionCatalog.load();
        }),
      ),
    { dispatch: false },
  );

  clearPermissions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoggedUserActions.logoutSuccess),
        tap(() => {
          this.permissionService.clear();
          this.permissionCatalog.clear();
        }),
      ),
    { dispatch: false },
  );

  setLoggedUserInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LoggedUserActions.setLoggedUserData),
      switchMap(() => {
        return this.userService.getMe().pipe(
          // Bridge into libs/ui (store-patterns.md): the banner and the
          // token interceptor read `ImpersonationService.meta`, which must
          // match what the backend just reported — not what the picker
          // cached before F5.
          tap(({ impersonation }) => this.impersonationService.syncMeta(impersonation)),
          map(({ user, permissions, impersonation }) => {
            return LoggedUserActions.loginSuccess({
              user,
              permissions,
              isSuperAdmin: false,
              tenantName: user.tenant?.name ?? '',
              impersonation,
            });
          }),
          catchError(async (err) => {
            const payload = await this.authService.getTokenPayload();
            const groups = (payload?.['cognito:groups'] as string[]) ?? [];
            if (groups.includes('SuperAdmin')) {
              // Ensure user attributes are loaded (avoids race with ngOnInit's loadCurrentUser)
              await this.authService.loadCurrentUser();
              const email = this.authService.currentUser()?.email ?? '';
              // Restore tenant name from sessionStorage (persisted at tenant-selection time)
              const tenantName = this.authService.getScopedTenantName();
              return LoggedUserActions.loginSuccess({
                user: {
                  name: email,
                  email,
                  lastname: '',
                  id: '',
                  roleId: '',
                  active: true,
                  confirmed: true,
                },
                permissions: [],
                isSuperAdmin: true,
                tenantName,
              });
            }
            // A normal user with N>1 `users` rows gets a 403 from
            // JwtAuthGuard on `GET /users/me` (BR-AUTH-01) — there is no
            // single tenant to resolve yet. Treat this as a TERMINAL
            // "success" (not an error) so LoginComponent still navigates;
            // authGuard independently re-checks membership count via
            // `GET /users/me/tenants` and redirects to /select-tenant.
            // Mirrors the pre-existing SuperAdmin placeholder-user pattern
            // above — same shape, empty permissions/tenantName until the
            // user actually picks a tenant.
            if ((err as { status?: number })?.status === 403) {
              await this.authService.loadCurrentUser();
              const email = this.authService.currentUser()?.email ?? '';
              return LoggedUserActions.loginSuccess({
                user: {
                  name: email,
                  email,
                  lastname: '',
                  id: '',
                  roleId: '',
                  active: true,
                  confirmed: true,
                },
                permissions: [],
                isSuperAdmin: false,
                tenantName: '',
              });
            }
            return LoggedUserActions.errorSettingUserLoggedData({error: extractError(err)});
          }),
        );
      }),
    );
  });
}
